import { FunctionComponent } from 'preact';
import { useEffect, useRef } from 'preact/hooks';

import { getAssetAccess } from '../../../utils/player/asset';
import { useTranslation } from '../../../utils/useTranslation';
import { Typography } from '../components/Typography/Typography';
import { useCurrentAsset } from '../hooks/CurrentAssetProvider';
import { useErrorRecovery } from '../hooks/ErrorRecoveryProvider';
import { usePlayerError } from '../hooks/PlayerErrorProvider';
import { useAccountVerification } from '../hooks/useAccountVerification';
import { useAdData } from '../hooks/useAdData';
import { usePaywallState } from '../hooks/usePaywallState';
import { usePlayer } from '../hooks/PlayerProvider';
import { usePlayerState } from '../hooks/usePlayerState';
import { useProvider } from '../hooks/useProvider';
import { useTimeToStartCountdown } from '../hooks/useTimeToStartCountdown';
import { useUpNext } from '../hooks/useUpNext';
import { getRealAssetFlightTimesStart } from '../utils/assetHelpers';
import { isEntitlementError } from '../utils/errorRecoverability';
import ns from '../translations';

import { useFloating } from '../hooks/FloatingProvider';
import { AdLayout } from './AdLayout/AdLayout';
import { DefaultLayout } from './DefaultLayout/DefaultLayout';
import { EndScreenLayout } from './EndScreenLayout/EndScreenLayout';
import { ErrorLayout } from './ErrorLayout/ErrorLayout';
import { FloatingLayout } from './FloatingLayout/FloatingLayout';
import { IdleLayout } from './IdleLayout/IdleLayout';
import { IdleLayoutElements } from './IdleLayout/IdleLayoutElements';
import { PauseAdLayout } from './PauseAdLayout/PauseAdLayout';
import { PaywallLayout } from './PaywallLayout/PaywallLayout';
import {
  PortalLinkLayout,
  shouldLinkToPortal,
} from './PortalLinkLayout/PortalLinkLayout';
import { TtsLayout } from './TtsLayout/TtsLayout';
import { UpcomingLayout } from './UpcomingLayout/UpcomingLayout';
import { UpNextPreviewLayout } from './UpNextPreviewLayout/UpNextPreviewLayout';
import { VerificationLayout } from './VerificationLayout/VerificationLayout';
import { isTTSContent } from '../utils/isTTSContent';
import { PodcastLayout } from './PodcastLayout/PodcastLayout';
import { PodcastPlayerOnlyLayout } from './PodcastLayout/PodcastPlayerOnlyLayout';
import { useConfig } from '../hooks/ConfigProvider';

export const VideoLayoutResolver: FunctionComponent = () => {
  const state = usePlayerState();
  const { configProvider, assetProvider } = useProvider();
  const { adData, isAdLoaded } = useAdData(undefined);
  const { asset } = useCurrentAsset();
  const { isPauseAdVisible } = useAdData(asset);
  const player = usePlayer();

  const { errorDetails } = usePlayerError();
  const { isAutoRetrying } = useErrorRecovery();
  const { t } = useTranslation(ns);
  const { upNextState, handleUpNextCancel } = useUpNext();
  const { hasAccess, isLoading: isIdentityLoading } = usePaywallState(
    getAssetAccess(asset)
  );
  const { isVerificationRequired } = useAccountVerification(configProvider);
  const { isFloating } = useFloating();

  const flightTimesStartInMilliseconds = asset
    ? getRealAssetFlightTimesStart(asset, true)
    : undefined;

  const { timeToStartInMilliseconds } = useTimeToStartCountdown(
    flightTimesStartInMilliseconds
  );

  const prevHasAccessRef = useRef(hasAccess);
  useEffect(() => {
    if (!prevHasAccessRef.current && hasAccess) {
      player?.trigger('accessGranted');
    }
    prevHasAccessRef.current = hasAccess;
  }, [hasAccess, player]);

  if (
    shouldLinkToPortal({
      playbackState: state,
      asset,
      provider: assetProvider,
      hasError: Boolean(errorDetails),
      hasAccess,
    })
  ) {
    return <PortalLinkLayout />;
  }

  if (
    asset &&
    flightTimesStartInMilliseconds &&
    timeToStartInMilliseconds > 0
  ) {
    return (
      <UpcomingLayout
        flightTimesStartInMilliseconds={flightTimesStartInMilliseconds}
        timeToStartInMilliseconds={timeToStartInMilliseconds}
        streamType={asset.streamType}
      />
    );
  }

  if (!hasAccess) {
    if (isIdentityLoading) {
      // Identity is still resolving — show the poster with a loading indicator
      // rather than immediately presenting the paywall. Once the initialization
      // window closes (same 4 s budget as the token request), the loading state
      // ends and we either show the paywall or dismiss it if access was granted.
      return (
        <div className="vgp-idle-layout vgp-idle-layout-not-started">
          <IdleLayoutElements asset={asset} isLoading />
        </div>
      );
    }
    return <PaywallLayout />;
  }

  if (isEntitlementError(errorDetails?.code)) {
    return <PaywallLayout />;
  }

  if (errorDetails) {
    if (isAutoRetrying) {
      return (
        <div className="vgp-reconnecting-layout">
          <Typography variant="label" size="large" color="white">
            {t('Reconnecting...')}
          </Typography>
        </div>
      );
    }
    return <ErrorLayout errorDetails={errorDetails} />;
  }

  if (isVerificationRequired) {
    return <VerificationLayout />;
  }

  if (isFloating) {
    return (
      <FloatingLayout
        adData={adData}
        upNextState={upNextState}
        handleUpNextCancel={handleUpNextCancel}
        isPauseAdVisible={isPauseAdVisible}
      />
    );
  }

  if (adData || isAdLoaded) {
    return <AdLayout adData={adData} />;
  }

  if (isPauseAdVisible) {
    return <PauseAdLayout />;
  }

  if (state === 'idle') {
    return <IdleLayout />;
  }

  if (upNextState === 'preview') {
    return <UpNextPreviewLayout onCancelClick={handleUpNextCancel} />;
  }

  if (upNextState === 'post-countdown') {
    return <EndScreenLayout onCancelClick={handleUpNextCancel} />;
  }

  return <DefaultLayout />;
};

export const AudioLayoutResolver: FunctionComponent = () => {
  const { asset } = useCurrentAsset();
  const { podcast } = useConfig();

  if (isTTSContent(asset)) {
    return <TtsLayout />;
  }

  if (podcast?.variant === 'player-only') {
    return <PodcastPlayerOnlyLayout />;
  }

  return <PodcastLayout />;
};
